import { OcclusionStudyMode, ReviewMask } from '../../../sdk/@types/Revisao';

/**
 * Copy and pure rules for the occlusion editor.
 *
 * The study mode lives on the card as `studyMode` (`Revisao.ts`). Absent means
 * `ocultar-todas` — the more restrictive of the two, same rule as a card saved
 * before the field existed.
 *
 * List order is the order the markings will be asked. Geometry stays on
 * `useMaskDraw` (percent of the rendered `<img>` box). This module never
 * measures the image.
 */

export const STUDY_MODE_OPTIONS: {
  value: OcclusionStudyMode;
  label: string;
  effect: string;
}[] = [
  {
    value: 'ocultar-todas',
    label: 'Ocultar todas',
    effect:
      'Ao perguntar uma marcação, todas as outras permanecem cobertas. O aluno não tem o contexto das demais respostas.',
  },
  {
    value: 'ocultar-uma',
    label: 'Ocultar uma',
    effect:
      'Ao perguntar uma marcação, as outras aparecem reveladas. O aluno vê as demais respostas como contexto.',
  },
];

/** Shown by the editor, and used to refuse a save, when the image has no markings. */
export const MASK_REQUIRED_MESSAGE = 'Ao menos uma marcação é necessária.';

/** Absent, or any value that is not one of the two, is the restrictive mode. */
export function resolveStudyMode(mode: OcclusionStudyMode | undefined): OcclusionStudyMode {
  return mode === 'ocultar-uma' ? 'ocultar-uma' : 'ocultar-todas';
}

/**
 * How many review units this image will produce: one per marking.
 * Zero markings have no count — the editor says `MASK_REQUIRED_MESSAGE` instead.
 */
export function reviewUnitsMessage(count: number): string | null {
  if (count <= 0) return null;
  if (count === 1) return 'Será gerada 1 unidade de revisão.';
  return `Serão geradas ${count} unidades de revisão.`;
}

/** Move the item at `from` to index `to`. Out-of-range or a no-op returns a copy. */
export function moveMask<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items.slice();
  }
  const next = items.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * Where `selected` lands after `moveMask`. The moved row keeps the selection;
 * rows that shift to make room follow it.
 */
export function indexAfterMove(
  selected: number | null,
  from: number,
  to: number,
  length: number,
): number | null {
  if (selected === null) return null;
  if (from === to || from < 0 || to < 0 || from >= length || to >= length) return selected;
  if (selected === from) return to;
  if (from < selected && selected <= to) return selected - 1;
  if (to <= selected && selected < from) return selected + 1;
  return selected;
}

export type EditorFace = 'edit' | 'front' | 'back';

/** What one marking shows once the author is previewing a student face. */
export type StudentMaskFace = 'covered' | 'revealed';

/**
 * Which marking the preview is asking. The selected row, or the first when
 * nothing is selected — the list order is the order they will be asked.
 */
export function askedIndex(selected: number | null, count: number): number {
  if (count <= 0) return 0;
  if (selected !== null && selected >= 0 && selected < count) return selected;
  return 0;
}

/**
 * Student face of one marking.
 *
 * Front, ocultar todas: every marking stays covered.
 * Front, ocultar uma: only the asked marking is covered; the others show their labels.
 * Back: the asked marking is revealed (that is the answer). The others stay
 * covered in ocultar todas, and stay revealed in ocultar uma.
 */
export function studentMaskFace(
  face: 'front' | 'back',
  mode: OcclusionStudyMode,
  index: number,
  asked: number,
): StudentMaskFace {
  const isAsked = index === asked;
  if (face === 'front') {
    if (mode === 'ocultar-todas') return 'covered';
    return isAsked ? 'covered' : 'revealed';
  }
  if (isAsked) return 'revealed';
  return mode === 'ocultar-uma' ? 'revealed' : 'covered';
}

/** Caption under the preview, so the face and the asked marking are explicit. */
export function previewCaption(face: 'front' | 'back', asked: number, total: number): string {
  const faceLabel = face === 'front' ? 'Frente' : 'Verso';
  return `${faceLabel} · perguntando a marcação ${asked + 1} de ${total}`;
}

export function relabelMask(masks: readonly ReviewMask[], index: number, label: string): ReviewMask[] {
  return masks.map((mask, i) => (i === index ? { ...mask, label } : mask));
}

export function removeMaskAt(masks: readonly ReviewMask[], index: number): ReviewMask[] {
  return masks.filter((_, i) => i !== index);
}

/** Selection after a removal. Removing the selected row clears it; rows after it shift down. */
export function indexAfterRemove(selected: number | null, removed: number): number | null {
  if (selected === null) return null;
  if (selected === removed) return null;
  if (selected > removed) return selected - 1;
  return selected;
}
